import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import { Doc, LoadingState, Stages, TrackingRows, User } from '@types'

export interface PatchRecoveredRejectedPayload {
  userId?: string
  files?: Doc[]
  stages?: Stages
}

interface analystRejectedState {
  loadingStatus: LoadingState
  message: string
  cases: User[]
}

const initialState: analystRejectedState = {
  loadingStatus: 'NOT_STARTED',
  message: '',
  cases: []
}

const analystRejectedSlice = createSlice({
  name: 'analystRejected',
  initialState,

  reducers: {
    getRecoveredRejectedCases: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getRecoveredRejectedCasesSuccess: (state, action: PayloadAction<User[]>) => {
      return {
        ...state,
        cases: action.payload,
        loadingStatus: 'SUCCESS'
      }
    },
    getRecoveredRejectedCasesFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    patchRecoveredRejectedInfo: (state, _action: PayloadAction<PatchRecoveredRejectedPayload>) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    clearAnalystRejected: _state => {
      return initialState
    }
  }
})

export const analystRejectedLoader = createDraftSafeSelector(
  [(state: GlobalState) => state.analystRejected.loadingStatus, (state: GlobalState) => state.analystRejected.message],
  (loadingStatus, message) => {
    const isLoading = loadingStatus === 'LOADING'
    const isFailed = loadingStatus === 'FAILED'
    const isSuccess = loadingStatus === 'SUCCESS'
    return { isLoading, isFailed, isSuccess, message }
  }
)

export const selectRecoveredRejectedCases = createDraftSafeSelector(
  [(state: GlobalState) => state.analystRejected.cases],
  cases => {
    const rows: TrackingRows[] = cases.map(person => ({
      ...person,
      id: person._id,
      parentName: person.parent?.name ? person.parent.name : '',
      bankName: person.bank?.toUpperCase(),
      studyDate: person.stages?.studyDate || '',
      office: person.stages?.office || '',
      bank: person.stages?.bank || '',
      bankDocument: person.bankDocument ?? '',
      success: person.stages?.success || '',
      feesOrPayments: person.stages?.feesOrPayments || '',
      comments: person.comments || '',
      isRejected: person?.isRejected || false,
      isRecovered: person?.isRecovered || false,
      createdAt: person.createdAt || '2020',
      fee: person.stages?.fee ?? 0,
      payments: person.stages?.payments ?? 0,
      delivered: person.stages?.delivered ?? '',
      received: person.stages?.received ?? '',
      returned: person.stages?.returned ?? '',
      rejected: person.stages?.rejected ?? ''
    }))

    return rows
  }
)

export const {
  getRecoveredRejectedCases,
  getRecoveredRejectedCasesSuccess,
  getRecoveredRejectedCasesFailed,
  patchRecoveredRejectedInfo,
  clearAnalystRejected
} = analystRejectedSlice.actions

export default analystRejectedSlice.reducer
